import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, User, Building2, CheckCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import axios from "axios";
import { format, addDays, isWeekend, isBefore, startOfDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    funding_amount: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const timeSlots = [
    "9:00 AM",
    "9:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime("");
  };

  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    return isWeekend(date) || isBefore(date, today);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const bookingData = {
        ...formData,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime
      };
      await axios.post(`${API}/bookings`, bookingData);
      setIsSubmitted(true);
    } catch (err) {
      setError("Failed to book appointment. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = selectedDate && selectedTime;
  const canProceedStep2 = formData.name && formData.email && formData.project_type;

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="booking-page">
      <Navigation />
      
      <main className="flex-1 relative z-10 px-8 lg:px-16 pt-40 pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 
              className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-white tracking-tight mb-6"
              data-testid="booking-heading"
            >
              Book a Partner Call
            </h1>
            <p className="text-lg font-light leading-relaxed text-slate-300 max-w-2xl mx-auto">
              Schedule a confidential consultation with one of our senior partners 
              to discuss your funding requirements.
            </p>
          </motion.div>

          {/* Progress Steps */}
          {!isSubmitted && (
            <div className="flex items-center justify-center mb-12">
              <div className="flex items-center gap-4">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        step >= s
                          ? "bg-[#bd9a59] text-slate-950"
                          : "bg-slate-800 text-slate-500"
                      }`}
                    >
                      {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                    </div>
                    {s < 3 && (
                      <div
                        className={`w-16 h-0.5 mx-2 transition-colors ${
                          step > s ? "bg-[#bd9a59]" : "bg-slate-700"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="glass-card p-8 rounded-sm">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="booking-success">
                  <CheckCircle className="w-16 h-16 text-[#bd9a59] mx-auto mb-6" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-white font-medium mb-4">
                    Booking Confirmed!
                  </h3>
                  <p className="text-slate-400 mb-2">
                    Your consultation has been scheduled for:
                  </p>
                  <p className="text-white text-lg font-medium mb-6">
                    {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")} at {selectedTime}
                  </p>
                  <p className="text-slate-400 mb-8">
                    A confirmation email has been sent to {formData.email}
                  </p>
                  <Button
                    onClick={() => {
                      setIsSubmitted(false);
                      setStep(1);
                      setSelectedDate(null);
                      setSelectedTime("");
                      setFormData({
                        name: "",
                        email: "",
                        phone: "",
                        company: "",
                        project_type: "",
                        funding_amount: "",
                        notes: ""
                      });
                    }}
                    className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-6 py-3"
                  >
                    Book Another Call
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-6">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Step 1: Date & Time Selection */}
                  {step === 1 && (
                    <div data-testid="booking-step-1">
                      <h2 className="font-['Playfair_Display'] text-xl text-white font-medium mb-6 flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-[#bd9a59]" />
                        Select Date & Time
                      </h2>
                      
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <Label className="text-slate-300 mb-3 block">Choose a Date</Label>
                          <div className="bg-slate-950/50 rounded-sm p-4 border border-white/10">
                            <CalendarComponent
                              mode="single"
                              selected={selectedDate}
                              onSelect={handleDateSelect}
                              disabled={isDateDisabled}
                              fromDate={new Date()}
                              toDate={addDays(new Date(), 60)}
                              className="text-white"
                              data-testid="booking-calendar"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-slate-300 mb-3 block">
                            {selectedDate 
                              ? `Available times on ${format(selectedDate, "MMMM d")}`
                              : "Select a date first"
                            }
                          </Label>
                          <div className="grid grid-cols-3 gap-2">
                            {timeSlots.map((time) => (
                              <button
                                key={time}
                                type="button"
                                disabled={!selectedDate}
                                onClick={() => setSelectedTime(time)}
                                className={`p-3 text-sm rounded-sm transition-all ${
                                  selectedTime === time
                                    ? "bg-[#bd9a59] text-slate-950 font-semibold"
                                    : selectedDate
                                    ? "bg-slate-800/50 text-slate-300 hover:bg-slate-700/50"
                                    : "bg-slate-800/30 text-slate-600 cursor-not-allowed"
                                }`}
                                data-testid={`time-slot-${time.replace(/[: ]/g, '-')}`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end mt-8">
                        <Button
                          type="button"
                          disabled={!canProceedStep1}
                          onClick={() => setStep(2)}
                          className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-6 py-3 disabled:opacity-50"
                          data-testid="booking-next-step-1"
                        >
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Personal Details */}
                  {step === 2 && (
                    <div data-testid="booking-step-2">
                      <h2 className="font-['Playfair_Display'] text-xl text-white font-medium mb-6 flex items-center gap-3">
                        <User className="w-5 h-5 text-[#bd9a59]" />
                        Your Details
                      </h2>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label htmlFor="name" className="text-slate-300 mb-2 block">
                            Full Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                            placeholder="John Smith"
                            data-testid="booking-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-slate-300 mb-2 block">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                            placeholder="john@company.com"
                            data-testid="booking-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                            placeholder="0400 000 000"
                            data-testid="booking-phone-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="company" className="text-slate-300 mb-2 block">
                            Company Name
                          </Label>
                          <Input
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                            placeholder="Your Company Pty Ltd"
                            data-testid="booking-company-input"
                          />
                        </div>
                      </div>

                      <div className="flex justify-between mt-8">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(1)}
                          className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-6 py-3"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back
                        </Button>
                        <Button
                          type="button"
                          disabled={!canProceedStep2}
                          onClick={() => setStep(3)}
                          className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-6 py-3 disabled:opacity-50"
                          data-testid="booking-next-step-2"
                        >
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Project Details */}
                  {step === 3 && (
                    <div data-testid="booking-step-3">
                      <h2 className="font-['Playfair_Display'] text-xl text-white font-medium mb-6 flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-[#bd9a59]" />
                        Project Details
                      </h2>

                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label htmlFor="project_type" className="text-slate-300 mb-2 block">
                            Project Type *
                          </Label>
                          <Select 
                            onValueChange={(value) => handleSelectChange("project_type", value)} 
                            value={formData.project_type}
                            required
                          >
                            <SelectTrigger 
                              className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                              data-testid="booking-project-type-select"
                            >
                              <SelectValue placeholder="Select project type" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-white/10">
                              <SelectItem value="residential_development">Residential Development</SelectItem>
                              <SelectItem value="commercial_development">Commercial Development</SelectItem>
                              <SelectItem value="mixed_use">Mixed Use</SelectItem>
                              <SelectItem value="land_acquisition">Land Acquisition</SelectItem>
                              <SelectItem value="construction_only">Construction Only</SelectItem>
                              <SelectItem value="refinance">Refinance</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="funding_amount" className="text-slate-300 mb-2 block">
                            Estimated Funding Required
                          </Label>
                          <Select 
                            onValueChange={(value) => handleSelectChange("funding_amount", value)} 
                            value={formData.funding_amount}
                          >
                            <SelectTrigger 
                              className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                              data-testid="booking-funding-amount-select"
                            >
                              <SelectValue placeholder="Select range" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-white/10">
                              <SelectItem value="under_5m">Under $5M</SelectItem>
                              <SelectItem value="5m_10m">$5M - $10M</SelectItem>
                              <SelectItem value="10m_25m">$10M - $25M</SelectItem>
                              <SelectItem value="25m_50m">$25M - $50M</SelectItem>
                              <SelectItem value="50m_100m">$50M - $100M</SelectItem>
                              <SelectItem value="over_100m">Over $100M</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mb-8">
                        <Label htmlFor="notes" className="text-slate-300 mb-2 block">
                          Additional Notes
                        </Label>
                        <Textarea
                          id="notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          rows={4}
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white resize-none"
                          placeholder="Any specific topics you'd like to discuss..."
                          data-testid="booking-notes-input"
                        />
                      </div>

                      {/* Summary */}
                      <div className="bg-slate-950/50 border border-white/10 rounded-sm p-6 mb-8">
                        <h3 className="text-white font-semibold mb-4">Booking Summary</h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-[#bd9a59]" />
                            <span className="text-slate-400">Date:</span>
                            <span className="text-white">
                              {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-[#bd9a59]" />
                            <span className="text-slate-400">Time:</span>
                            <span className="text-white">{selectedTime}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <User className="w-4 h-4 text-[#bd9a59]" />
                            <span className="text-slate-400">Name:</span>
                            <span className="text-white">{formData.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Building2 className="w-4 h-4 text-[#bd9a59]" />
                            <span className="text-slate-400">Project:</span>
                            <span className="text-white">{formData.project_type?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(2)}
                          className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-6 py-3"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSubmitting || !formData.project_type}
                          className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-8 py-3 disabled:opacity-50"
                          data-testid="booking-submit-btn"
                        >
                          {isSubmitting ? "Booking..." : "Confirm Booking"}
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingPage;
